/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { useEffect, useRef } from 'react';
import EventBus from '../utils/EventBus';
import { handleSetIframeContent, replaceAppWithPayloadHTMl, processAndExecuteScripts } from '../utils/handlers';
import { EventActions, DYNAMIC_CONTENT_ATTR } from '../utils/constants';
import { getLogger } from '../utils/debugging';
const logger = getLogger();

/**
 * This is a custom hook that establishes an Interframe connection
 * with the host app to set the HTML content of the #content-embed container.
 */
export function useContentEmbed(hostOrigin, hostWindow, id, embedOptions) {
  const dimensions = useRef({
    width: 0,
    height: 0
  });
  useEffect(() => {
    // Create the event bus for communication
    let eventBus = null;
    if (hostWindow && id) {
      eventBus = new EventBus({
        id
      });
      eventBus.connect(hostOrigin, hostWindow);
    }

    // This handler ignores resizedEls/mutationsList/onresize events because
    // we are always just looking for dimension changes of the entire document
    const handleResize = () => {
      const computedStyles = getComputedStyle(document.body.parentElement);
      const width = parseInt(computedStyles.width, 10);
      const height = parseInt(computedStyles.height, 10);
      const {
        width: curWidth,
        height: curHeight
      } = dimensions.current;
      // No need to tell the parent since the document dimensions did not change
      if (width === curWidth && height === curHeight) return;
      const payload = {
        width,
        height
      };

      // Tell the parent application the document dimensions has changed
      if (eventBus) {
        eventBus.postMessage({
          action: EventActions.SIZE_CHANGE,
          payload
        });
      }

      // Keep track of the new document dimensions
      dimensions.current = payload;
    };
    let resizeObserver;
    let mutationObserver;
    if (window.ResizeObserver) {
      // Use ResizeObserver whenever possible
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(document.body);
    } else {
      // Otherwise relying on MutationObserver + window.onresize (for IE)
      // suffices for the most part (more dynamic content, such as script
      // embeds, might contain a few dimension-changing lapses)
      mutationObserver = new MutationObserver(handleResize);
      mutationObserver.observe(document.body, {
        attributes: true,
        childList: true,
        subtree: true
      });
      window.addEventListener('resize', handleResize);
    }

    // Flag any dynamically added nodes with the DYNAMIC_CONTENT_ATTR
    // so we can easily cleanup the <head/> and <body/> cruft
    const nodeMutationObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (!mutation.addedNodes.length) return;
        Array.from(mutation.addedNodes).forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE && node instanceof HTMLElement && node.id !== 'hs-embed-content') {
            node.setAttribute(DYNAMIC_CONTENT_ATTR, 'true');
          }
        });
      });
    });
    nodeMutationObserver.observe(document.head, {
      childList: true
    });
    nodeMutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
    const setContentCallback = () => {
      // Tell the parent we have finished setting the embed content
      if (eventBus) {
        eventBus.postMessage({
          action: EventActions.SET_CONTENT_DONE
        });
      }
    };
    if (eventBus) {
      eventBus.on(EventActions.SET_CONTENT, data => {
        if (id) {
          logger(id, 'received embed content', data);
        }
        if (embedOptions && embedOptions.shouldReplaceAllHTML) {
          replaceAppWithPayloadHTMl(data);
          processAndExecuteScripts(data, setContentCallback);
        } else {
          handleSetIframeContent(data, setContentCallback, embedOptions);
        }
      });
    }
    const disconnect = () => {
      if (id) {
        logger(id, 'is shutting down...');
      }
      // Cleanup all observers and the event bus
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
      nodeMutationObserver.disconnect();
      if (eventBus) {
        eventBus.disconnect();
      }
    };
    if (eventBus) {
      eventBus.on(EventActions.DISCONNECT, disconnect);
    }

    // Tell the parent application we are ready to receive content
    if (id) {
      logger(id, 'is READY!');
    }
    if (eventBus) {
      eventBus.postMessage({
        action: EventActions.READY
      });
    }
    return disconnect;
  }, [id, hostOrigin, hostWindow, embedOptions]);
  return null;
}